import { useState } from "react";
import { Mail, MapPin, Check, Loader2, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import BookingForm from "@/components/BookingForm";
import GlobalContactBlock from "@/components/GlobalContactBlock";
import { ALCHAMIS_PRIMARY_EMAIL, ALCHAMIS_CONTACT_EMAIL } from "@/components/FlagIcon";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { SOLUTIONS } from "@/data/content";

const INTERESTS = ["Consulting Services", "AI & Digital Transformation", ...SOLUTIONS.map((s) => s.name), "General Inquiry"];

export default function Contact() {
  const [mode, setMode] = useState("message"); // "message" | "schedule"
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", interest: "", message: "" });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contacts", form);
      setSuccess(true);
      toast.success("Your message has been received. A partner will follow up shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <span className="overline">Contact</span>
          <h1 className="font-display text-5xl sm:text-6xl text-[#001B54] mt-6 leading-[1.05]">
            Begin a private
            <span className="block italic gold-text">conversation.</span>
          </h1>
          <p className="text-[#3F4A6B] mt-6 leading-relaxed max-w-md">
            For media, partnership, or engagement inquiries, write to our office or use the form.
            Every message is reviewed by a partner.
          </p>
          <div className="mt-12 space-y-4">
            <div className="flex gap-4 items-start p-5 border border-[#001B54]/10">
              <MapPin className="text-gold mt-1" size={18} />
              <div>
                <div className="text-[#001B54]">90 E Halsey Rd, Parsippany, Suite 332</div>
                <div className="text-[#3F4A6B] text-sm">New Jersey, USA</div>
              </div>
            </div>
            <div className="flex gap-4 items-center p-5 border border-[#001B54]/10">
              <Mail className="text-gold" size={18} />
              <a className="text-[#001B54] hover:text-gold break-all" href={`mailto:${ALCHAMIS_PRIMARY_EMAIL}`} data-testid="contact-email">
                {ALCHAMIS_PRIMARY_EMAIL}
              </a>
            </div>
            <div className="flex gap-4 items-center p-5 border border-[#001B54]/10">
              <Mail className="text-gold" size={18} />
              <a className="text-[#001B54] hover:text-gold break-all" href={`mailto:${ALCHAMIS_CONTACT_EMAIL}`} data-testid="contact-email-secondary">
                {ALCHAMIS_CONTACT_EMAIL}
              </a>
            </div>
            <GlobalContactBlock variant="stack" testIdPrefix="contact-phone" />
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <div className="flex gap-2 mb-4" role="tablist" aria-label="Contact options">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "message"}
              onClick={() => setMode("message")}
              data-testid="contact-mode-message"
              className={`px-4 py-2.5 border text-[0.7rem] tracking-[0.18em] uppercase transition-all ${
                mode === "message" ? "bg-gold text-navy border-gold" : "bg-transparent text-[#3F4A6B] border-[#001B54]/12 hover:border-gold/40"
              }`}
            >
              Send a Message
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "schedule"}
              onClick={() => setMode("schedule")}
              data-testid="contact-mode-schedule"
              className={`px-4 py-2.5 border text-[0.7rem] tracking-[0.18em] uppercase transition-all ${
                mode === "schedule" ? "bg-gold text-navy border-gold" : "bg-transparent text-[#3F4A6B] border-[#001B54]/12 hover:border-gold/40"
              }`}
            >
              Schedule a Briefing
            </button>
          </div>
          {mode === "schedule" ? (
            <BookingForm source="contact" testIdPrefix="contact-booking" />
          ) : success ? (
            <div data-testid="contact-success" className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-12 text-center min-h-[480px] flex flex-col items-center justify-center">
              <Check className="text-gold" size={36} />
              <div className="font-display text-3xl text-[#001B54] mt-6">Message received.</div>
              <p className="text-[#3F4A6B] max-w-md mt-4">
                Your inquiry has been logged in our private CRM. A partner will respond within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-8 md:p-10 space-y-4">
              <div className="overline">Private Inquiry</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-input-name"
                  className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3.5 text-sm text-[#001B54]" />
                <input required type="email" placeholder="Business email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-input-email"
                  className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3.5 text-sm text-[#001B54]" />
                <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="contact-input-company"
                  className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3.5 text-sm text-[#001B54]" />
                <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="contact-input-phone"
                  className="bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3.5 text-sm text-[#001B54]" />
              </div>
              <div>
                <div className="overline mb-3">Area of Interest</div>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setForm({ ...form, interest: i })}
                      data-testid={`contact-interest-${i}`}
                      className={`px-3.5 py-2 border text-[0.7rem] tracking-[0.14em] uppercase transition-all duration-300 ${
                        form.interest === i ? "bg-gold text-navy border-gold" : "bg-transparent text-[#3F4A6B] border-[#001B54]/12 hover:border-gold/50"
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              </div>
              <textarea required placeholder="How can we help?" rows={5} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-input-message"
                className="w-full bg-white border border-[#001B54]/12 focus:border-gold/50 outline-none px-4 py-3.5 text-sm text-[#001B54]" />
              <button type="submit" disabled={sending} className="luxury-button justify-center w-full sm:w-auto" data-testid="contact-submit">
                {sending ? <><Loader2 size={14} className="animate-spin" /> Sending</> : <>Send Inquiry <ArrowRight size={14} /></>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
